import { Link } from 'react-router-dom';

// Category-specific hero content
const HERO_CONTENT = {
  'Modular Office Workstation': {
    label: 'Premium Collection',
    title: 'Modular Office',
    titleAccent: 'Workstations',
    description: 'Precision-engineered workspace solutions crafted for the modern enterprise. German technology meets artisanal excellence.',
    image: 'https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/moitgllc_Large%20BAnner%20Image%20of%20Workstations.png',
    stats: [
      { value: '5000', suffix: '+', label: 'Offices' },
      { value: '50K', suffix: '+', label: 'Workstations' },
      { value: '10', suffix: '+', label: 'Years' }
    ],
    badge: { line1: 'Factory Direct', line2: 'Premium Quality' }
  },
  'Privacy Booths': {
    label: 'Acoustic Excellence',
    title: 'Privacy',
    titleAccent: 'Booths',
    description: 'Experience the miracle of noise reduction. Soundproof pods designed for focused work, private calls, and undisturbed productivity.',
    image: 'https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/odymdcyd_Large%20Banner%20image%20pf%20Phone%20Booth.png',
    stats: [
      { value: '35', suffix: 'dB', label: 'Noise Reduction' },
      { value: '500', suffix: '+', label: 'Installed' },
      { value: '100', suffix: '%', label: 'Satisfaction' }
    ],
    badge: { line1: 'Soundproof', line2: 'Focus Pods' }
  },
  'Storage Cabinets': {
    label: 'Organized Excellence',
    title: 'Storage',
    titleAccent: 'Cabinets',
    description: 'Premium pedestals and storage solutions with lockable drawers. Crafted in walnut, white, and silver finishes for the modern workspace.',
    image: 'https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/w4s8qize_image.png',
    stats: [
      { value: '3', suffix: '+', label: 'Drawer Options' },
      { value: '5', suffix: '+', label: 'Finishes' },
      { value: '100', suffix: '%', label: 'Lockable' }
    ],
    badge: { line1: 'Secure Storage', line2: 'Premium Finish' }
  }
};

export function CinematicCategoryHero({ category }) {
  const content = HERO_CONTENT[category];
  
  // Only render for supported categories
  if (!content) return null;

  return (
    <section className="relative bg-[#f8f7f4] min-h-[450px] overflow-hidden">
      {/* Decorative accent line */}
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#c5a059] via-[#c5a059]/50 to-transparent" />
      
      {/* Content Grid */}
      <div className="relative z-10 container mx-auto px-8 py-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[450px]">
        
        {/* Left: Text Content */}
        <div className="space-y-6">
          <div>
            <p className="text-[#c5a059] uppercase tracking-[0.3em] text-xs font-semibold mb-4">
              {content.label}
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl text-[#1a1a1a] leading-tight mb-4" >
              {content.title}<br />
              <span className="text-[#c5a059] italic">{content.titleAccent}</span>
            </h1>
            <p className="text-gray-600 text-base lg:text-lg leading-relaxed max-w-md">
              {content.description}
            </p>
          </div>
          
          {/* Stats Row */}
          <div className="flex gap-8 lg:gap-12 pt-4 border-t border-gray-200">
            {content.stats.map((stat, index) => (
              <div key={index}>
                <p className="text-2xl lg:text-3xl font-bold text-[#1a1a1a]">
                  {stat.value}<span className="text-[#c5a059]">{stat.suffix}</span>
                </p>
                <p className="text-xs text-gray-500 uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 pt-2">
            <a 
              href="#products" 
              className="bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a] px-6 lg:px-8 py-3 lg:py-4 font-bold uppercase tracking-wider transition-all text-sm"
            >
              View Collection
            </a>
            <Link 
              to="/bulk-inquiry" 
              className="border border-[#1a1a1a] hover:border-[#c5a059] hover:text-[#c5a059] text-[#1a1a1a] px-6 lg:px-8 py-3 lg:py-4 font-medium uppercase tracking-wider transition-all text-sm"
            >
              Get Quote
            </Link>
          </div>
        </div>
        
        {/* Right: Product Image with Effects */}
        <div className="relative hidden lg:block">
          {/* Main Image */}
          <div className="relative">
            <img 
              src={content.image}
              alt={`${content.title} ${content.titleAccent}`}
              className={`w-full rounded-lg shadow-lg ${content.imageScale || ''}`}
            />
            {/* Accent border */}
            <div className="absolute -bottom-2 -right-2 w-full h-full border border-[#c5a059]/30 rounded-lg" />
          </div>
          
          {/* Floating badge */}
          <div className="absolute -bottom-4 -left-4 bg-[#c5a059] text-[#1a1a1a] px-5 py-3">
            <p className="text-xs font-bold uppercase tracking-wider">{content.badge.line1}</p>
            <p className="text-base font-black">{content.badge.line2}</p>
          </div>
        </div>

        {/* Mobile Image */}
        <div className="relative lg:hidden">
          <img 
            src={content.image}
            alt={`${content.title} ${content.titleAccent}`}
            className="w-full rounded-lg shadow-lg"
          />
        </div>
      </div>
      
      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c5a059]/50 to-transparent" />
    </section>
  );
}

export default CinematicCategoryHero;
